import { useRouter } from 'next/router'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import urljoin from 'url-join'
import AuthService from "../service/auth"
import { actions } from '../store/actions'
import { RootState } from "../store/reducers/reducers"
import { replaceAll } from '../utils/string'
import API from '../service/api'

const LAST_CHOSEN_REGION_KEY = 'indo4ward:last_chosen_region';
const REGION_PROMPT_DISMISSED_KEY = 'indo4ward:region_prompt_dismissed';

const getPromptDismissed = (): boolean => {
	if (typeof window === 'undefined') return false;
	const expiryStr = window.localStorage.getItem(REGION_PROMPT_DISMISSED_KEY);
	if (!expiryStr) return false;

	const expiry = parseInt(expiryStr, 10);
	const now = new Date().getTime();
	if (isNaN(expiry) || now > expiry) {
		window.localStorage.removeItem(REGION_PROMPT_DISMISSED_KEY);
		return false;
	}
	return true;
};

export const setPromptDismissed = () => {
	const expiry = new Date().getTime() + 24 * 60 * 60 * 1000; // 24 hours
	window.localStorage.setItem(REGION_PROMPT_DISMISSED_KEY, expiry.toString());
};

let hasAttemptedDetection = false;

export interface RegionInfo {
	isLoading: boolean;
	countryCode: string;
}

export function useRegionCountryCode(): RegionInfo {
	const router = useRouter()
	const signedIn = AuthService.useIsLoggedIn()
	const tfaPassed = AuthService.useHasCompletedTfa()

	const { query } = router
	const routerCountryCode = Array.isArray(query.countryCode) ? query.countryCode[0] : query.countryCode;

	const state = useSelector((state: RootState) => state.region)
	const {
		defaultRegion,
		defaultRegionLoaded,
		preferredRegion,
		preferredRegionLoaded
	} = state;
	let lastChosenRegion = state.lastChosenRegion;
	if (!lastChosenRegion && typeof window !== 'undefined') {
		lastChosenRegion = window.sessionStorage.getItem(LAST_CHOSEN_REGION_KEY);
	}

	const dispatch = useDispatch();

	React.useEffect(() => {
		if (!defaultRegionLoaded) {
			// load default region
			dispatch(actions.region.fetchDefaultRegion());
		}
	}, [defaultRegionLoaded, dispatch]);

	React.useEffect(() => {
		if (!preferredRegionLoaded && signedIn && tfaPassed) {
			dispatch(actions.region.fetchPreferredRegion());
		}
	}, [preferredRegionLoaded, signedIn, tfaPassed, dispatch]);

	const chosenRegion = routerCountryCode || preferredRegion || lastChosenRegion || defaultRegion || '';

	React.useEffect(() => {
		if (!chosenRegion || typeof window === 'undefined') {
			return;
		}
		const promptDismissed = getPromptDismissed();

		if (!promptDismissed && !hasAttemptedDetection) {
			hasAttemptedDetection = true;
			API.region.detectCountryCode().then(res => {
				const detectedCountryCode = res.data.countryCode;
				if (detectedCountryCode && chosenRegion && detectedCountryCode.toUpperCase() !== chosenRegion.toUpperCase()) {
					dispatch(actions.region.showRegionSwitchModal(detectedCountryCode));
				}
			}).catch(e => {
				// Fail silently
				console.error('Failed to detect country code', e);
			});
		}
	}, [chosenRegion, dispatch, router]);

	const isLoading = !chosenRegion;
	if (isLoading) {
		return {
			isLoading: true,
			countryCode: ''
		};
	}
	
	const countryCode = chosenRegion.toUpperCase()
	if (typeof window !== 'undefined') {
		window.sessionStorage.setItem(LAST_CHOSEN_REGION_KEY, countryCode);
	}
	return {
		countryCode,
		isLoading: false
	};
}

export function useSetPreferredRegion() {
	const dispatch = useDispatch()
	return (newCurrentRegion: string) => dispatch(actions.region.setPreferredRegion(newCurrentRegion))
}

export function useLinksWithRegion(links: string[]) {
	const regionInfo = useRegionCountryCode()
	if (regionInfo.isLoading) {
		return links
	}

	return links.map(link => {
		return urljoin('/', regionInfo.countryCode, link)
	})
}

export function useRegionLinkPrefix() {
	const regionInfo = useRegionCountryCode()
	if (regionInfo.isLoading) {
		return ''
	}

	return '/' + regionInfo.countryCode
}

export function useRegionLinkSubber() {
	const regionLinkPrefix = useRegionLinkPrefix()

	return React.useCallback((urlString: string) => {
		// const isAbs = urlString.indexOf('http://') === 0 || urlString.indexOf('https://') === 0
		// if (!isAbs) {
		// 	const isRootPath = urlString.indexOf('/') === 0
		// 	if (!isRootPath) {
		// 		return urlString
		// 	}
		// 	return regionLinkPrefix + urlString
		// }

		const countryCode = regionLinkPrefix.substring(1)
		return replaceAll(urlString, '${country}', countryCode)
	}, [regionLinkPrefix])
}